import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import PageLoader from "./components/PageLoader";
import SideFormPopout from "./components/SideFormPopout";

// Lazy load all page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ServiceDetailPage = lazy(() => import("./pages/ServiceDetailPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const MeetTheTeamPage = lazy(() => import("./pages/MeetTheTeamPage"));
const TestimonialsPage = lazy(() => import("./pages/TestimonialsPage"));
const OurProcessPage = lazy(() => import("./pages/OurProcessPage"));
const ServiceAreasPage = lazy(() => import("./pages/ServiceAreasPage"));
const ServiceAreaDetailPage = lazy(() => import("./pages/ServiceAreaDetailPage"));
const CommunityDetailPage = lazy(() => import("./pages/CommunityDetailPage"));
const ResidentialPage = lazy(() => import("./pages/ResidentialPage"));
const CommercialPage = lazy(() => import("./pages/CommercialPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PortfolioPage = lazy(() => import("./pages/PortfolioPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const BrandPartnersPage = lazy(() => import("./pages/BrandPartnersPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const FormSubmissionsPage = lazy(() => import("./pages/FormSubmissionsPage"));
const BlogManagementPage = lazy(() => import("./pages/BlogManagementPage"));
const BlogEditorPage = lazy(() => import("./pages/BlogEditorPage"));
const PortfolioManagementPage = lazy(() => import("./pages/PortfolioManagementPage"));
const PortfolioEditorPage = lazy(() => import("./pages/PortfolioEditorPage"));
const ServiceSupportPage = lazy(() => import("./pages/ServiceSupportPage"));
const FinancingPage = lazy(() => import("./pages/FinancingPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <SideFormPopout />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/services/:slug" element={<ServiceDetailPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/team" element={<MeetTheTeamPage />} />
              <Route path="/testimonials" element={<TestimonialsPage />} />
              <Route path="/process" element={<OurProcessPage />} />
              <Route path="/service-areas" element={<ServiceAreasPage />} />
              <Route path="/service-areas/:areaSlug" element={<ServiceAreaDetailPage />} />
              <Route path="/service-areas/:areaSlug/:communitySlug" element={<CommunityDetailPage />} />
              <Route path="/residential" element={<ResidentialPage />} />
              <Route path="/commercial" element={<CommercialPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/portfolio" element={<PortfolioPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/partners" element={<BrandPartnersPage />} />
              <Route path="/support" element={<ServiceSupportPage />} />
              <Route path="/financing" element={<FinancingPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/system/submissions" element={<FormSubmissionsPage />} />
              <Route path="/system/blog" element={<BlogManagementPage />} />
              <Route path="/system/blog/:id" element={<BlogEditorPage />} />
              <Route path="/system/portfolio" element={<PortfolioManagementPage />} />
              <Route path="/system/portfolio/:id" element={<PortfolioEditorPage />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
